import { DocumentType, DOCUMENT_GROUPS } from "./DocumentConstants";

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export interface FileWithType {
  file: File;
  fileType: string;
  progress: number;
  stage:
    | "idle"
    | "uploading"
    | "extracting"
    | "updating"
    | "complete"
    | "failed";
  errorMessage?: string;
  applicationId?: string;
}

export interface UploadResult {
  path: string;
  fileName: string;
  fileType?: string;
  applicationId?: string;
  contentHash?: string;
}

export const DocumentTypesEnum = Object.values(DOCUMENT_GROUPS)
  .flatMap((g) => g.types)
  .reduce(
    (acc, type) => ({ ...acc, [type]: type }),
    {} as Record<string, string>,
  );

export const fileTypeOptions = (): Array<{
  value: string;
  label: string;
}> => {
  return Object.keys(DocumentTypesEnum).map((enumValue) => ({
    value: enumValue.toLowerCase().replace(/_/g, ""),
    label: enumValue
      .split("_")
      .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
      .join(" "),
  }));
};

export const fileTypeOptionsGrouped = () => {
  return Object.entries(DOCUMENT_GROUPS).map(([label, group]) => ({
    label,
    options: group.types.map((key: string) => ({
      value: key.toLowerCase().replace(/_/g, ""),
      label: key
        .split("_")
        .map((w: string) => w.charAt(0) + w.slice(1).toLowerCase())
        .join(" "),
    })),
  }));
};

const generateDocumentTypeLabels = (): Record<string, string> => {
  const labels: Record<string, string> = {};

  Object.values(DOCUMENT_GROUPS).forEach((group) => {
    group.types.forEach((docType) => {
      labels[docType] = docType
        .split("_")
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(" ");
    });
  });

  return labels;
};

const DOCUMENT_TYPE_LABELS = generateDocumentTypeLabels();

export const getDocumentTypeLabel = (type: string): string => {
  return DOCUMENT_TYPE_LABELS[type] || type;
};

export const convertDocumentTypeToEnumValue = (type: string): string => {
  return (
    Object.values(DocumentType).find(
      (v) => v.toLowerCase().replace(/_/g, "") === type,
    ) ?? type
  );
};

export interface PreviewFormValues {
  // Preference
  plannedLevelOfEducation: string;
  preferredAreaOfStudy: string;
  preferredStudyDestination: string;

  // Personal Information - BasicDetails
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: Date | null;
  gender: string;
  countryOfBirth: string;
  countryOfCitizenship: string;
  maritalStatus: string;
  registeredDateOfMarriage: Date | null;

  // Personal Information - ContactDetails
  phoneCountryCode: string;
  phoneAreaCode: string | null;
  phoneNumber: string;
  phoneExtension: string | null;

  mobileCountryCode: string;
  mobileAreaCode: string | null;
  mobileNumber: string;
  mobileExtension: string | null;

  currentAddressStreet1: string;
  currentAddressStreet2: string;
  currentAddressCity: string;
  currentAddressState: string;
  currentAddressPostalCode: string;
  currentAddressCountry: string;

  // Personal Information - PassportDetails
  passportNumber: string;
  passportExpiryDate: Date | null;
  passportIssuingCountry: string;

  // Personal Information - VisaDetails;
  currentVisaType: string;
  visaExpiryDate: Date | null;

  // English Language Test Scores
  englishTest: string;
  overallScore: number | null;
  reading: number | null;
  writing: number | null;
  speaking: number | null;
  listening: number | null;
  useOfEnglish: number | null;
  dateOfCompletion: Date | null;
  testReportFormNumber: string;

  // High School
  highSchoolName: string;
  highSchoolCity: string;
  highSchoolCountry: string;
  highSchoolGradeObtained: string;
  highSchoolStartDate: Date | null;
  highSchoolEndDate: Date | null;

  // Tertiary Education (Multiple entries)
  tertiary1School: string;
  tertiary1Course: string;
  tertiary1City: string;
  tertiary1Country: string;
  tertiary1GradeObtained: string;
  tertiary1StartDate: Date | null;
  tertiary1EndDate: Date | null;
  tertiary2School: string;
  tertiary2Course: string;
  tertiary2City: string;
  tertiary2Country: string;
  tertiary2GradeObtained: string;
  tertiary2StartDate: Date | null;
  tertiary2EndDate: Date | null;
  tertiary3School: string;
  tertiary3Course: string;
  tertiary3City: string;
  tertiary3Country: string;
  tertiary3GradeObtained: string;
  tertiary3StartDate: Date | null;
  tertiary3EndDate: Date | null;

  // Scholarship Financial Support
  scholarshipProviderName: string;

  // Health Insurance
  oshcProviderName: string;
  oshcMembershipNumber: string;
  oshcExpiryDate: Date | null;

  // Work Experience (Multiple entries)
  employment1CompanyName: string;
  employment1PositionAndDuties: string;
  employment1DateCommenced: Date | null;
  employment1DateEnded: Date | null;
  employment2CompanyName: string;
  employment2PositionAndDuties: string;
  employment2DateCommenced: Date | null;
  employment2DateEnded: Date | null;
  employment3CompanyName: string;
  employment3PositionAndDuties: string;
  employment3DateCommenced: Date | null;
  employment3DateEnded: Date | null;
}
